import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router';
import { api, type Page, type Query } from '../lib/api';
import { ErrorNotice } from '../ui/feedback';
import { DataTable, type Column } from '../ui/table';
import { ItemId, Position, Time } from '../ui/values';
import { paths } from './paths';
import type { ActivityEntry, AdminArea, AdminChestRow, AdminPlanSummary, AuditEntry } from './types';

/** Gemeinsame Bausteine der Admin-Seiten. */

/** Zurück zur Liste mit den Filtern, mit denen man sie verlassen hat (design.md §5). */
export function BackLink({ to, label }: { to: string; label: string }) {
  const state = useLocation().state as { from?: string } | null;
  return <Link to={state?.from?.startsWith(to) ? state.from : to}>‹ {label}</Link>;
}

/** Filterwert in der URL; Ändern setzt die Pagination zurück. */
export function useUrlFilter(key: string) {
  const [params, setParams] = useSearchParams();
  const value = params.get(key) ?? '';
  const set = (v: string) =>
    setParams(
      (p) => {
        const q = new URLSearchParams(p);
        if (v) q.set(key, v);
        else q.delete(key);
        q.delete('cursor');
        q.delete('back');
        return q;
      },
      { replace: true },
    );
  return [value, set] as const;
}

/** Suchfeld, das erst nach kurzer Pause in die URL schreibt. */
export function FilterSearch({ value, onChange, label }: { value: string; onChange: (v: string) => void; label: string }) {
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);
  useEffect(() => {
    if (local === value) return;
    const t = setTimeout(() => onChange(local.trim()), 300);
    return () => clearTimeout(t);
  }, [local, value, onChange]);
  return <input type="search" aria-label={label} placeholder={label} value={local} onChange={(e) => setLocal(e.target.value)} />;
}

/**
 * Seitenweise Abfrage für Tabellen in Reitern (Cursor im Zustand, nicht in der URL).
 * Für Listenseiten mit Filtern in der URL dient `useKeysetPage` aus ui/table.
 */
export function useLocalPaged<T>(key: unknown[], path: string, query: Query) {
  const [stack, setStack] = useState<string[]>([]);
  const cursor = stack[stack.length - 1];
  const q = useQuery({
    queryKey: [...key, cursor],
    queryFn: () => api.get<Page<T>>(path, { ...query, cursor, limit: 25 }),
    placeholderData: keepPreviousData,
  });
  return {
    q,
    hasPrev: stack.length > 0,
    prev: () => setStack((s) => s.slice(0, -1)),
    next: () => q.data?.nextCursor && setStack((s) => [...s, q.data!.nextCursor!]),
  };
}

export function LocalPager({ paged }: { paged: { hasPrev: boolean; prev: () => void; next: () => void; q: { data?: { nextCursor: string | null } } } }) {
  if (!paged.hasPrev && !paged.q.data?.nextCursor) return null;
  return (
    <nav className="pager" aria-label="Seiten">
      <button type="button" onClick={paged.prev} disabled={!paged.hasPrev}>
        ‹ Zurück
      </button>
      <button type="button" onClick={paged.next} disabled={!paged.q.data?.nextCursor}>
        Weiter ›
      </button>
    </nav>
  );
}

/** Kisten-Tabelle für Reiter (Spieler, Team) und die Kistenliste. */
export function chestColumns(opts: { owner?: boolean } = {}): Column<AdminChestRow>[] {
  return [
    { key: 'name', header: 'Name', cell: (c) => c.name ?? <span className="muted">ohne Namen</span> },
    ...(opts.owner === false
      ? []
      : [
          { key: 'owner', header: 'Besitzer', cell: (c: AdminChestRow) => c.owner.name ?? <span className="muted">–</span> },
          { key: 'team', header: 'Team', hide: 'md' as const, cell: (c: AdminChestRow) => c.team?.name ?? <span className="muted">–</span> },
        ]),
    { key: 'server', header: 'Server', hide: 'sm', cell: (c) => <span className="mono">{c.server}</span> },
    { key: 'area', header: 'Area', hide: 'md', cell: (c) => <AreaLabel name={c.area} /> },
    { key: 'pos', header: 'Position', cell: (c) => <Position dimension={c.dimension} pos={c.pos} /> },
    { key: 'variants', header: 'Varianten', num: true, hide: 'md', cell: (c) => c.variantCount },
    { key: 'updated', header: 'aktualisiert', hide: 'sm', cell: (c) => <Time iso={c.contentsUpdatedAt} /> },
  ];
}

export function ChestTab({ query, owner }: { query: Query; owner?: boolean }) {
  const paged = useLocalPaged<AdminChestRow>(['admin', 'chests', query], '/admin/chests', query);
  return (
    <div className="stack">
      {paged.q.isError && <ErrorNotice error={paged.q.error} onRetry={() => paged.q.refetch()} />}
      <DataTable
        label="Kisten"
        rows={paged.q.data?.items}
        loading={paged.q.isPending}
        rowKey={(c) => c.id}
        rowHref={(c) => paths.chest(c.id)}
        empty="Keine Kisten."
        columns={chestColumns({ owner })}
      />
      <div className="section-head">
        <LocalPager paged={paged} />
        <Link to={`${paths.chests}?${new URLSearchParams(query as Record<string, string>)}`}>In der Kistenliste öffnen</Link>
      </div>
    </div>
  );
}

const auditLabel = (action: string) => action.replace(/^admin\./, '');

/** Audit-Einträge zu einem Ziel (Spieler, Team, Kiste). Grund aus `details.reason`. */
export function AuditTab({ targetType, targetId }: { targetType: string; targetId: string }) {
  const query = { targetType, targetId };
  const paged = useLocalPaged<AuditEntry>(['admin', 'audit', query], '/admin/audit-log', query);
  return (
    <div className="stack">
      {paged.q.isError && <ErrorNotice error={paged.q.error} onRetry={() => paged.q.refetch()} />}
      <DataTable
        label="Audit-Log"
        rows={paged.q.data?.items}
        loading={paged.q.isPending}
        rowKey={(a) => a.id}
        empty="Keine Admin-Aktionen."
        columns={[
          { key: 'at', header: 'Zeit', cell: (a) => <Time iso={a.createdAt} /> },
          { key: 'action', header: 'Aktion', cell: (a) => <span className="mono">{auditLabel(a.action)}</span> },
          { key: 'actor', header: 'Akteur', cell: (a) => a.actor?.name ?? <span className="muted">System</span> },
          { key: 'reason', header: 'Grund', cell: (a) => (typeof a.details.reason === 'string' ? a.details.reason : <span className="muted">–</span>) },
        ]}
      />
      <LocalPager paged={paged} />
    </div>
  );
}

/** Spieler-Aktionen auf Deutsch (Backend: `src/lib/activity.ts`). */
export const ACTIVITY_LABELS: Record<string, string> = {
  'auth.register': 'Konto angelegt',
  'auth.login': 'Mod angemeldet',
  'auth.logout': 'Mod abgemeldet',
  'auth.web_link': 'Browser-Link geholt',
  'web.login': 'Browser angemeldet',
  'web.logout': 'Browser abgemeldet',
  'device.revoke': 'Gerät abgemeldet',
  'discord.unlink': 'Discord gelöst',
  'chest.register': 'Kiste registriert',
  'chest.batch': 'Massen-Upload',
  'chest.rename': 'Kiste umbenannt',
  'chest.delete': 'Kiste entfernt',
  'chest.contents': 'Inhalt geändert',
  'chest.area': 'Area geändert',
  'ender.write': 'Endertruhe geschrieben',
  'ender.delete': 'Endertruhe gelöscht',
  'container.write': 'Container geschrieben',
  'container.delete': 'Container gelöscht',
  'area.personal_delete': 'Personal Storage entfernt',
  'settings.update': 'Einstellungen geändert',
  'area.icon': 'Area-Symbol geändert',
  'area.rename': 'Area umbenannt',
  'area.merge': 'Areas zusammengelegt',
  'sortplan.write': 'Sortierplan gespeichert',
  'sortplan.delete': 'Sortierplan gelöscht',
  'team.create': 'Team erstellt',
  'team.join': 'Team beigetreten',
  'team.leave': 'Team verlassen',
  'team.rename': 'Team umbenannt',
  'team.join_code': 'Join-ID erneuert',
  'team.transfer': 'Team übergeben',
  'team.member_remove': 'Mitglied entfernt',
  'team.dissolve': 'Team aufgelöst',
};

const num = (v: unknown) => (typeof v === 'number' ? v : 0);

/** Kurzbeschreibung der wichtigsten Details; alles Weitere zeigt die aufklappbare Zeile im Protokoll. */
export function activitySummary(a: ActivityEntry): string {
  const d = a.details;
  switch (a.action) {
    case 'chest.register':
      return `${d.created ? 'neu' : 'schon vorhanden'} · ${String(d.server ?? '')}`;
    case 'chest.contents':
      return `Version ${num(d.version)} · ${num(d.variants)} Sorten`;
    case 'chest.batch':
      return `${num(d.total)} Einträge · ${num(d.created)} neu · ${num(d.written)} Inhalte · ${num(d.failed) + num(d.conflicts) + num(d.contentErrors)} abgelehnt`;
    case 'chest.rename':
    case 'team.create':
    case 'team.join':
    case 'team.rename':
      return typeof d.name === 'string' ? d.name : '';
    case 'chest.area':
      return d.area ? String(d.area) : 'Default';
    case 'area.icon':
      return `${String(d.name ?? '')} · ${d.icon ? String(d.icon) : 'ohne Symbol'}`;
    case 'area.rename':
      return `${String(d.from ?? '')} ⇒ ${String(d.to ?? '')} · ${num(d.chests)} Kisten`;
    case 'area.merge':
      return `${String(d.from ?? '')} ⇒ ${d.into ? String(d.into) : 'Default'} · ${num(d.chests)} Kisten`;
    case 'sortplan.write':
      return `${d.area ? String(d.area) : 'Default'} · Revision ${num(d.revision)} · ${num(d.chests)} Kisten`;
    case 'sortplan.delete':
      return d.area ? String(d.area) : 'Default';
    case 'web.login':
      return d.method === 'discord' ? 'per Discord' : d.method === 'microsoft' ? 'per Microsoft' : 'per Einmal-Link';
    case 'auth.register':
    case 'auth.login':
      return typeof d.clientVersion === 'string' ? d.clientVersion : '';
    default:
      return '';
  }
}

/** Link zum Ziel einer Aktion (Kiste, Team, Spieler), sonst nichts. */
export function ActivityTarget({ a }: { a: ActivityEntry }) {
  if (!a.targetId) return <span className="muted">–</span>;
  if (a.targetType === 'chest') return <Link to={paths.chest(a.targetId)}>Kiste</Link>;
  if (a.targetType === 'team') return <Link to={paths.team(a.targetId)}>Team</Link>;
  if (a.targetType === 'user') return <Link to={paths.player(a.targetId)}>Spieler</Link>;
  return <span className="muted">Gerät</span>;
}

/** Reiter „Aktivität“ im Spieler-Detail. */
export function ActivityTab({ userId }: { userId: string }) {
  const query = { userId };
  const paged = useLocalPaged<ActivityEntry>(['admin', 'activity', query], '/admin/activity', query);
  return (
    <div className="stack">
      {paged.q.isError && <ErrorNotice error={paged.q.error} onRetry={() => paged.q.refetch()} />}
      <DataTable
        label="Aktivität"
        rows={paged.q.data?.items}
        loading={paged.q.isPending}
        rowKey={(a) => a.id}
        empty="Keine Aktivität in den letzten 90 Tagen."
        columns={[
          { key: 'at', header: 'Zeit', cell: (a) => <Time iso={a.createdAt} /> },
          { key: 'action', header: 'Aktion', cell: (a) => ACTIVITY_LABELS[a.action] ?? <span className="mono">{a.action}</span> },
          { key: 'info', header: 'Details', hide: 'sm', cell: (a) => activitySummary(a) || <span className="muted">–</span> },
          { key: 'target', header: 'Ziel', cell: (a) => <ActivityTarget a={a} /> },
        ]}
      />
      <LocalPager paged={paged} />
    </div>
  );
}

/** Area-Name, `''` als „Default“ (Revision R4). */
export function AreaLabel({ name }: { name: string }) {
  return name ? <>{name}</> : <span className="muted">Default</span>;
}

/** Reiter „Areas“ in Team- bzw. Spieler-Detail: Bereiche mit Symbol und Kistenzahl. */
export function AreasTab({ query }: { query: { teamId: string } | { ownerId: string } }) {
  const q = useQuery({
    queryKey: ['admin', 'areas', query],
    queryFn: () => api.get<{ items: AdminArea[] }>('/admin/areas', query),
  });
  return (
    <div className="stack">
      {q.isError && <ErrorNotice error={q.error} onRetry={() => q.refetch()} />}
      <DataTable
        label="Areas"
        rows={q.data?.items}
        loading={q.isPending}
        rowKey={(a) => `${a.server}|${a.name}`}
        empty="Keine Areas."
        columns={[
          { key: 'name', header: 'Area', cell: (a) => <AreaLabel name={a.name} /> },
          { key: 'icon', header: 'Symbol', hide: 'sm', cell: (a) => (a.icon ? <ItemId id={a.icon} /> : <span className="muted">–</span>) },
          { key: 'server', header: 'Server', hide: 'sm', cell: (a) => <span className="mono">{a.server}</span> },
          {
            key: 'chests',
            header: 'Kisten',
            num: true,
            cell: (a) =>
              a.name && a.chestCount > 0 ? (
                <Link to={`${paths.chests}?${new URLSearchParams({ ...query, server: a.server, area: a.name })}`}>{a.chestCount}</Link>
              ) : (
                a.chestCount
              ),
          },
        ]}
      />
    </div>
  );
}

/** Reiter „Sortierpläne“ in Team- bzw. Spieler-Detail. */
export function PlansTab({ query }: { query: { teamId: string } | { ownerId: string } }) {
  const paged = useLocalPaged<AdminPlanSummary>(['admin', 'sort-plans', query], '/admin/sort-plans', query);
  return (
    <div className="stack">
      {paged.q.isError && <ErrorNotice error={paged.q.error} onRetry={() => paged.q.refetch()} />}
      <DataTable
        label="Sortierpläne"
        rows={paged.q.data?.items}
        loading={paged.q.isPending}
        rowKey={(p) => p.id}
        rowHref={(p) => paths.sortPlan(p.id)}
        empty="Keine Sortierpläne."
        columns={[
          { key: 'area', header: 'Area', cell: (p) => <AreaLabel name={p.area} /> },
          { key: 'pos', header: 'Dimension', hide: 'sm', cell: (p) => <span className="mono">{p.dimension}</span> },
          { key: 'server', header: 'Server', hide: 'md', cell: (p) => <span className="mono">{p.server}</span> },
          { key: 'rev', header: 'Revision', num: true, cell: (p) => p.revision },
          { key: 'chests', header: 'Kisten', num: true, cell: (p) => p.chestCount },
          { key: 'by', header: 'zuletzt von', hide: 'md', cell: (p) => p.updatedBy?.name ?? <span className="muted">–</span> },
          { key: 'at', header: 'aktualisiert', hide: 'sm', cell: (p) => <Time iso={p.updatedAt} /> },
        ]}
      />
      <LocalPager paged={paged} />
    </div>
  );
}

/** Variante kurz: Anzeigename, sonst die Komponenten als kompaktes JSON (voll im Tooltip). */
export function VariantLabel({ variant }: { variant: { name: string | null; components: Record<string, unknown> | null } }) {
  if (!variant.components) return <>{variant.name ?? <span className="muted">Basis</span>}</>;
  const json = JSON.stringify(variant.components);
  return (
    <span className="variant">
      {variant.name && <span>{variant.name} </span>}
      <span className="mono muted" title={json}>
        {json.length > 60 ? `${json.slice(0, 57)}…` : json}
      </span>
    </span>
  );
}
